import {ElementRef, Injectable} from '@angular/core';
import {dateFormat} from '@js-dot/core/date';
import {CSV} from '@js-dot/data/csv';

const _rest = /,/g;

@Injectable({
	providedIn: 'root'
})
/**
 * 내려받기 서비스
 */
export class ContentDownloadService {
	/**
	 * <A>를 통해 내려 받기 실행
	 * @param content
	 * @param filename
	 * @protected
	 */
	protected _execute(content: Blob, filename: string) {
		const a         = document.createElement('a');
		a.download      = filename;
		a.href          = window.URL.createObjectURL(content);
		a.style.display = 'none';
		document.body.append(a);
		a.click();
		setTimeout(() => {
			document.body.removeChild(a);
		});
	}

	/**
	 * 시간 기반 파일명 얻기
	 * @param extension
	 * @protected
	 */
	protected _timeBasedFilename(extension?) {
		let filename = dateFormat('Y-m-d His');
		if (extension) {
			filename += '.' + extension;
		}
		return filename;
	}

	/**
	 * <table> 얻기
	 * @param elementRef
	 * @protected
	 */
	protected _getTable(elementRef: ElementRef<Element>);
	protected _getTable(elementRef) {
		const target = elementRef.nativeElement;
		return target.tagName === 'TABLE' ?
			target :
			target.getElementsByTagName('TABLE')[0];
	}

	/**
	 * <table>로 부터 CSV 내려 받기
	 * @param elementRef
	 */
	tableToCsv(elementRef: ElementRef<Element>);
	tableToCsv(elementRef) {
		const table  = this._getTable(elementRef);
		const {rows} = table;

		const rowEnd = rows.length;
		// ignore header
		let rowIndex = 0;
		let row;
		let cols;
		let col;
		let colEnd;
		let colIndex;

		const lines = [];
		let line    = [];

		// header
		row      = rows[0];
		cols     = row.cells;
		colEnd   = cols.length;
		colIndex = -1;

		while (++colIndex < colEnd) {
			col = cols[colIndex];
			line.push(col.innerText);
		}
		lines.push(line.join(','));

		while (++rowIndex < rowEnd) {
			line     = [];
			row      = rows[rowIndex];
			cols     = row.cells;
			colEnd   = cols.length;
			colIndex = -1;

			while (++colIndex < colEnd) {
				col = cols[colIndex];
				line.push(col.innerText.replace(_rest, ''));
			}
			lines.push(line.join(','));
		}

		const csv     = lines.join('\n');
		const csvFile = new Blob([csv], {type: 'text/csv'});

		this._execute(csvFile, this._timeBasedFilename('csv'));
	}

	/**
	 * <table>로 부터 XLS 내려 받기
	 * @param elementRef
	 */
	tableToXls(elementRef: ElementRef<Element>);
	tableToXls(elementRef) {
		const table   = this._getTable(elementRef);
		const content = table.outerHTML;
		this.xls(content, this._timeBasedFilename('xls'));
	}

	/**
	 * Binary로 내려 받기
	 * @param bytes
	 * @param filename
	 */
	bytes(bytes: Uint8Array, filename?: string) {
		const blob = new Blob([bytes], {
			type: 'application/octet-stream'
		});

		this._execute(blob, filename);
	}

	download(mimeType: string, content: string, filename: string | number, extension?: string) {
		filename = '' + filename;

		if (extension) {
			extension = '.' + extension;
			if (!filename.endsWith(extension)) {
				filename += extension;
			}
		}
		const file = new Blob([content], {type: mimeType});
		this._execute(file, filename || this._timeBasedFilename(extension));
	}

	/**
	 * 내려받기 시작
	 * @param file
	 * @param filename
	 */
	execute(file: Blob, filename: string) {
		this._execute(file, filename);
	}

	/**
	 * XML 팡리로 내려 받기
	 * @param content
	 * @param filename
	 */
	xml(content: string, filename: string | number) {
		this.download('text/xml', content, filename, 'xml');
	}

	/**
	 * CSV 파일로 내려 받기
	 * @param content
	 * @param filename
	 */
	csv(content: string, filename: string | number) {
		this.download('text/csv;charset=UTF-8', '\uFEFF' + content, filename, 'csv');
	}

	/***
	 * XLS 파일로 내려 받기
	 * @param content
	 * @param filename
	 */
	xls(content: string, filename?: string | number) {
		this.download('application/vnd.ms-excel', content, filename, 'xls');
	}

	/***
	 * CSV를 제공하는 주소로 부터 XLS 파일을 생성, 내려 받기
	 * @param url
	 * @param filename
	 */
	xlsFromCsvUrl(url: string, filename: string | number = null) {
		fetch(
			url,
			{
				method: 'get',
				headers: {
					'Content-Type': 'csv'
				}
			}
		)
			.then(response => {
				return response.text();
			})
			.then(csv => {
				const rows    = CSV.parse(csv);
				const content = [];

				const end   = rows.length;
				let current = -1;
				while (++current < end) {
					const row               = rows[current];
					content[content.length] = '<tr><td>' + row.join('</td><td>') + '</td></tr>';
				}

				content.unshift('<table>');
				content.push('</table>');

				this.xls(content.join(''), filename);
			});
	}


	/**
	 * SVG 문자열로 부터 PNG 내려 받기
	 */
	svgToPng(svg: string, width: number, height: number, filename?: string) {
		const canvas  = document.createElement('canvas');
		canvas.width  = width;
		canvas.height = height;
		const context = canvas.getContext('2d');

		// import('canvg')
		// 	.then(({Canvg}) => {
		// 		return Canvg.fromString(context, svg).render();
		// 	})
		// 	.then(() => {
		// 		const a    = document.createElement('a');
		// 		a.download = filename;
		// 		a.href     = canvas.toDataURL();
		// 		a.click();
		// 	})
	}
}
